import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Table, Button, Skeleton, Switch } from 'antd';
import { PlusOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { getAllClients, pushClientsToBackend } from '../../services/clientsService';
import type { Client } from '../../types/client';
import { clientsEn } from '../../content/clients.en';
import styles from './Clients.module.css';

const today = (): string => new Date().toISOString().slice(0, 10);

const hasOnlyPastTrips = (client: Client): boolean => {
  const trips = client.trips ?? [];
  if (trips.length === 0) return false;
  const hasAnyDates = trips.some((t) => t.startDate || t.endDate);
  if (!hasAnyDates) return false;
  const now = today();
  return trips.every((t) => {
    const end = t.endDate ?? t.startDate;
    const start = t.startDate ?? t.endDate;
    return (!end || end < now) && (!start || start < now);
  });
};

const Clients: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading, isDataSyncing } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [showPastClients, setShowPastClients] = useState(false);
  const hasPushedRef = useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.sub && !isDataSyncing) {
      try {
        const list = getAllClients(user.sub);
        const arr = Array.isArray(list) ? list : [];
        const sorted = [...arr].sort((a, b) => {
          const lnA = (a.lastName ?? '').toLowerCase();
          const lnB = (b.lastName ?? '').toLowerCase();
          if (lnA !== lnB) return lnA.localeCompare(lnB);
          return (a.firstName ?? '').toLowerCase().localeCompare((b.firstName ?? '').toLowerCase());
        });
        setClients(sorted);
        // Push existing localStorage data to backend on first load
        if (!hasPushedRef.current) {
          hasPushedRef.current = true;
          pushClientsToBackend(user.sub);
        }
      } catch {
        setClients([]);
      }
    } else if (!isAuthenticated) {
      setClients([]);
    }
  }, [isAuthenticated, user?.sub, isDataSyncing]);

  const displayedClients = showPastClients ? clients : clients.filter((c) => !hasOnlyPastTrips(c));

  const columns = [
    {
      title: clientsEn.columns.name,
      key: 'name',
      render: (_: unknown, record: Client) => {
        const name = [record.firstName, record.lastName].filter(Boolean).join(' ') || '—';
        return (
          <Link to={`/clients/${record.id}`} onClick={(e) => e.stopPropagation()}>
            {name}
          </Link>
        );
      },
    },
    {
      title: clientsEn.columns.dates,
      key: 'dates',
      render: (_: unknown, record: Client) => {
        const firstTrip = record.trips?.[0];
        if (!firstTrip) return '—';
        return clientsEn.tripDates(firstTrip.startDate, firstTrip.endDate);
      },
    },
    {
      title: clientsEn.columns.trips,
      key: 'trips',
      render: (_: unknown, record: Client) => {
        const count = record.trips?.length ?? 0;
        return count > 0 ? String(count) : '—';
      },
    },
  ];

  if (authLoading || isDataSyncing) {
    return (
      <div className={styles.clients}>
        <div className={styles.container}>
          <div className={styles.header}>
            <Skeleton.Input active style={{ width: 120 }} />
            <Skeleton.Button active />
          </div>
          <Skeleton active paragraph={{ rows: 4 }} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.clients}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h1 className={styles.title}>{clientsEn.pageTitle}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Switch
                checked={showPastClients}
                onChange={setShowPastClients}
                checkedChildren={clientsEn.showPastClients}
                unCheckedChildren={clientsEn.hidePastClients}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button
              icon={<UnorderedListOutlined />}
              onClick={() => navigate('/list-builder')}
            >
              {clientsEn.goToListBuilder}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/clients/new')}
            >
              {clientsEn.addClient}
            </Button>
          </div>
        </div>

        {clients.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>{clientsEn.noClients}</div>
            <div className={styles.emptyDescription}>
              {clientsEn.noClientsDescription}
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/clients/new')}
            >
              {clientsEn.addClient}
            </Button>
          </div>
        ) : displayedClients.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>{clientsEn.filteredOutPast}</div>
            <Button onClick={() => setShowPastClients(true)}>
              {clientsEn.showPastClientsLabel}
            </Button>
          </div>
        ) : (
          <Table
            dataSource={Array.isArray(displayedClients) ? displayedClients : []}
            columns={columns}
            rowKey="id"
            pagination={false}
            childrenColumnName="__no_children__"
            onRow={(record) => ({
              onClick: () => navigate(`/clients/${record.id}`),
              style: { cursor: 'pointer' },
            })}
          />
        )}
      </div>
    </div>
  );
};

export default Clients;
